import "./formDatePicker.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { createRef, Fragment, PureComponent } from "react";

import {
  DATE_CHANGE_FUNCTIONS,
  DATE_FIELD_END,
  DATE_FIELD_START,
  DATE_FIELDS,
  getDateFieldTitle
} from "../../../constants/dateFields";
import KeyConstants from "../../../constants/keyConstants";
import PubSubTopics from "../../../constants/pubSubTopics";
import { isDateValid } from "../../../modules/definition/calendar/dateFunctions";
import { dateWithString, endDateWithString } from "../../../modules/definition/calendar/dateFunctions/dateWithString";
import { stringFromDate, stringFromEndDate } from "../../../modules/definition/calendar/dateFunctions/stringFromDate";
import {
  setItemEarliestEndDate,
  setItemPreferredEndDate
} from "../../../modules/items/reducers/byId/itemDates/setEndDate";
import { setItemLatestStartDate } from "../../../modules/items/reducers/byId/itemDates/setItemLatestStartDate";
import { setItemPreferredStartDate } from "../../../modules/items/reducers/byId/itemDates/setItemPreferredStartDate";
import { immutableRecords, PropTypeShapes } from "../../../types";
import { DateRecord } from "../../../types/immutableRecords/model/data/DateRecord";
import { getDatesOnPrecisionChange } from "../../../utils/calendarPicker";
import { wrapEditingComponent } from "../../../utils/codeSplit";
import isEditable from "../../../utils/isEditable";
import { getValueWithNone } from "../../../utils/labelUtils";
import localUndoUtils from "../../../utils/localUndoUtils";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import uniqueId from "../../../utils/uniqueId";
import CalendarPicker from "../../calendarPicker";
import CalendarPickerPopover from "../../calendarPicker/CalendarPickerPopover";
import Icon from "../../interface/Icon";
import { withPortalChildContext } from "../../portal/PortalChildContext";
import FormTextField from "../FormTextField";

const getDatesRecordFromProps = useProps => {
  return immutableRecords.ItemDatesRecord({
    startDate: useProps.startDate,
    preferredStartDate: useProps.startDate,
    duration: useProps.duration,
    preferredDuration: useProps.duration,
    endDate: useProps.endDate,
    preferredEndDate: useProps.endDate,
    latestStartDate: useProps.latestStartDate,
    earliestEndDate: useProps.earliestEndDate,
    durationToEarliestEnd: useProps.durationToEarliestEnd
  });
};

const getDateStringValue = (value, calendar, isEndDate, startDate, showOngoing) => {
  if (showOngoing) {
    return "Present";
  }

  // If no date, return empty string
  if (!isDateValid(value)) {
    return "";
  }
  // Calculate the date that needs to be displayed
  // based on whether this is an end date or a start date
  if (isEndDate) {
    return stringFromEndDate(calendar, value, startDate);
  }
  return stringFromDate(calendar, value);
};

const CALENDAR_PICKER_DATES_KEYS = [
  "startDate",
  "duration",
  "endDate",
  "latestStartDate",
  "earliestEndDate",
  "durationToEarliestEnd"
];

const isAnyDateValueChanged = (props, calendarPickerDates) => {
  return Boolean(CALENDAR_PICKER_DATES_KEYS.find(k => props[k] !== calendarPickerDates[k]));
};

class FormDatePicker extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      initialValue: "",
      value: "",
      showCalendar: false,
      selectedDateField: this.props.selectedDateField,
      calendarPickerDates: getDatesRecordFromProps(this.props)
    };
    this._uncommittedChange = false;
    this._unprocessedCalendarPickerChanges = false;

    this._fieldRef = createRef();
    this.targetId = `formDatePicker-${uniqueId()}`;
    this.inputId = `input-${uniqueId()}`;

    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.showCalendar = this.showCalendar.bind(this);
    this.hideCalendar = this.hideCalendar.bind(this);
    this.onFocusField = this.onFocusField.bind(this);

    // FormDatePicker maintains its own undoStack just for its calendar picker
    // It is easier to handle this here than inside calendar picker, since this is
    // the ultimate keeper of the values
    this.clearUndoStack = this.clearUndoStack.bind(this);
    this.updateUndoStack = this.updateUndoStack.bind(this);
    this.undoChange = this.undoChange.bind(this);
    this.redoChange = this.redoChange.bind(this);
    this.startListeningForUndo = this.startListeningForUndo.bind(this);
    this.stopListeningForUndo = this.stopListeningForUndo.bind(this);

    this.onCalendarPickerStartDateChange = this.onCalendarPickerStartDateChange.bind(this);
    this.onCalendarPickerEndDateChange = this.onCalendarPickerEndDateChange.bind(this);
    this.onCalendarPickerLatestStartDateChange = this.onCalendarPickerLatestStartDateChange.bind(this);
    this.onCalendarPickerEarliestEndDateChange = this.onCalendarPickerEarliestEndDateChange.bind(this);
    this.onCalendarPickerPrecisionChange = this.onCalendarPickerPrecisionChange.bind(this);
    this.updateCalendarPickerDatesInternally = this.updateCalendarPickerDatesInternally.bind(this);

    this.onInitialSlidePortalTransitionEnd = this.onInitialSlidePortalTransitionEnd.bind(this);
  }

  componentWillMount() {
    const value = getDateStringValue(
      this.props[this.props.selectedDateField],
      this.props.calendar,
      this.isEndDate(),
      this.props.startDate,
      this.props.ongoing && this.isPreferredEndDate()
    );
    this.setState({ value: value, initialValue: value });
    this._uncommittedChange = false;
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      // If we have a PortalContext, then we want the portal to be in charge of when we focus
      if (this.props.portalChildContext && this.props.portalChildContext.registerTransitionEndCallback) {
        this.props.portalChildContext.registerTransitionEndCallback(this.onInitialSlidePortalTransitionEnd, true);
      } else {
        this.focus();
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    // And now replace our old values with the newly received values
    const nextValue = nextProps[nextProps.selectedDateField];

    const value = getDateStringValue(
      nextValue,
      nextProps.calendar,
      this.isEndDate(),
      nextProps.startDate,
      nextProps.ongoing && this.isPreferredEndDate()
    );

    if (this.state.initialValue !== value || isAnyDateValueChanged(nextProps, this.state.calendarPickerDates)) {
      // If there are changes to commit, we need to commit them before the new props
      // roll in...
      this.commitChange();
      this.setState({
        value: value,
        initialValue: value,
        calendarPickerDates: getDatesRecordFromProps(nextProps)
      });
      this._uncommittedChange = false;
      this._unprocessedCalendarPickerChanges = false;
    }
  }

  componentWillUnmount() {
    // If there are changes to commit, we need to commit them before the component unmounts
    // Otherwise, we could lose the edits on actions like changing view types
    this.commitChange();
    this.stopListeningForUndo();
    this.stopListeningToSlidePortal();
  }

  stopListeningToSlidePortal() {
    if (this.props.portalChildContext && this.props.portalChildContext.unregisterTransitionEndCallback) {
      this.props.portalChildContext.unregisterTransitionEndCallback(this.onInitialSlidePortalTransitionEnd);
    }
  }

  onInitialSlidePortalTransitionEnd() {
    this.stopListeningToSlidePortal();
    if (this.props.autoFocus) {
      this.focus();
    }
  }

  onCalendarPickerPrecisionChange(newPrecision, changeEndDates) {
    const datesOnPrecisionChange = getDatesOnPrecisionChange(
      newPrecision,
      changeEndDates,
      this.props.calendar,
      this.state.calendarPickerDates,
      this.props.autoAdjustEndDate
    );
    const calendarPickerDates = datesOnPrecisionChange.calendarPickerDates;
    const datesChanged = datesOnPrecisionChange.datesChanged;
    if (datesChanged) {
      this.updateCalendarPickerDatesInternally(calendarPickerDates);
    }
  }

  onCalendarPickerStartDateChange(date) {
    const calendar = this.props.calendar;
    const calendarPickerDates = setItemPreferredStartDate(
      this.state.calendarPickerDates,
      date,
      calendar,
      this.props.autoAdjustEndDate,
      true
    );
    this.updateCalendarPickerDatesInternally(calendarPickerDates);
  }

  onCalendarPickerLatestStartDateChange(date) {
    // If it is not a valid date, then remove the latestStartDate altogether
    if (!isDateValid(date)) {
      const calendarPickerDates = setItemLatestStartDate(this.state.calendarPickerDates, null, null, calendar);
      this.updateCalendarPickerDatesInternally(calendarPickerDates);
      return;
    }

    // Otherwise, make sure it is inside a legal range
    const startDate = this.state.calendarPickerDates.preferredStartDate;
    const earliestEndDate = this.state.calendarPickerDates.earliestEndDate;
    const endDate = this.state.calendarPickerDates.preferredEndDate;

    let allowChange = true;
    const calendar = this.props.calendar;
    if (isDateValid(startDate) && date.timestamp < startDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(earliestEndDate) && date.timestamp > earliestEndDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(endDate) && date.timestamp > endDate.timestamp) {
      allowChange = false;
    }

    const calendarPickerDates = allowChange
      ? setItemLatestStartDate(this.state.calendarPickerDates, date.timestamp, date.precision, calendar)
      : this.state.calendarPickerDates;
    this.updateCalendarPickerDatesInternally(calendarPickerDates);
  }

  onCalendarPickerEarliestEndDateChange(date) {
    // If it is not a valid date, then remove the latestStartDate altogether
    if (!isDateValid(date)) {
      const calendarPickerDates = setItemEarliestEndDate(this.state.calendarPickerDates, null, null, calendar);
      this.updateCalendarPickerDatesInternally(calendarPickerDates);
      return;
    }

    // Otherwise, make sure it is inside a legal range
    const startDate = this.state.calendarPickerDates.preferredStartDate;
    const latestStartDate = this.state.calendarPickerDates.latestStartDate;
    const endDate = this.state.calendarPickerDates.preferredEndDate;
    let allowChange = true;
    const calendar = this.props.calendar;
    if (isDateValid(startDate) && date.timestamp < startDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(latestStartDate) && date.timestamp < latestStartDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(endDate) && date.timestamp > endDate.timestamp) {
      allowChange = false;
    }

    const calendarPickerDates = allowChange
      ? setItemEarliestEndDate(this.state.calendarPickerDates, date.timestamp, date.precision, calendar)
      : this.state.calendarPickerDates;
    this.updateCalendarPickerDatesInternally(calendarPickerDates);
  }

  onCalendarPickerEndDateChange(date) {
    // If it is not a valid date, then remove the latestStartDate altogether
    if (!isDateValid(date)) {
      const calendarPickerDates = setItemPreferredEndDate(this.state.calendarPickerDates, null, null, calendar);
      this.updateCalendarPickerDatesInternally(calendarPickerDates);
      return;
    }

    // Otherwise, make sure it is inside a legal range
    const startDate = this.state.calendarPickerDates.preferredStartDate;
    const latestStartDate = this.state.calendarPickerDates.latestStartDate;
    const earliestEndDate = this.state.calendarPickerDates.earliestEndDate;

    let allowChange = true;
    const calendar = this.props.calendar;
    if (isDateValid(startDate) && date.timestamp < startDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(latestStartDate) && date.timestamp < latestStartDate.timestamp) {
      allowChange = false;
    } else if (isDateValid(earliestEndDate) && date.timestamp < earliestEndDate.timestamp) {
      allowChange = false;
    }
    const calendarPickerDates = allowChange
      ? setItemPreferredEndDate(this.state.calendarPickerDates, date.timestamp, date.precision, calendar)
      : this.state.calendarPickerDates;
    this.updateCalendarPickerDatesInternally(calendarPickerDates);
  }

  updateCalendarPickerDatesInternally(calendarPickerDates) {
    this._unprocessedCalendarPickerChanges = true;
    this.setState({ calendarPickerDates });
    this.updateUndoStack(calendarPickerDates);
  }

  isEndDate() {
    return this.props.selectedDateField.toLowerCase().includes("end");
  }

  isPreferredEndDate() {
    return this.props.selectedDateField === DATE_FIELD_END;
  }

  // TODO: Add validation of element before allowing change...?
  onChange(value) {
    this._uncommittedChange = true;
    this.setState({ value });
  }

  focus() {
    if (this._fieldRef && this._fieldRef.current) {
      this._fieldRef.current.focus();
    }
  }

  toDate() {
    if (this.state.value.length) {
      if (this.isEndDate()) {
        return endDateWithString(this.props.calendar, this.state.value, this.props.startDate, this.props.duration);
      }
      return dateWithString(this.props.calendar, this.state.value);
    }
    return DateRecord();
  }

  onKeyDown(e) {
    switch (e.key) {
      case KeyConstants.Tab:
      case KeyConstants.Enter: {
        this.commitChange();
        this.hideCalendar();
        break;
      }
      case KeyConstants.Up:
      case KeyConstants.Down:
      case KeyConstants.Left:
      case KeyConstants.Right:
        e.stopPropagation();
        break;
    }
  }

  onBlur(_e) {
    this.commitChange();
  }

  commitChange() {
    if (this.state.showCalendar && this._unprocessedCalendarPickerChanges) {
      this.processCalendarPickerChanges();
    }
    if (this._uncommittedChange && this.state.value !== this.state.initialValue) {
      const date = this.toDate();
      if (date) {
        this.props[DATE_CHANGE_FUNCTIONS[this.props.selectedDateField]](date);
        this.setState({
          value: getDateStringValue(
            this.props[this.props.selectedDateField],
            this.props.calendar,
            this.isEndDate(),
            this.props.startDate,
            this.props.ongoing && this.isPreferredEndDate()
          )
        });
      } else {
        this.setState({ value: this.state.initialValue });
      }
    }
    this._uncommittedChange = false;
  }

  showCalendar() {
    if (!this.state.showCalendar) {
      // Prevent iOS from scrolling the calendar and opening keyboard on open
      //#!if iOS != true
      this.focus();
      //#!endif
      this._unprocessedCalendarPickerChanges = false;
      this.setState({ showCalendar: true });
      this.startListeningForUndo();
      if (this.props.onToggleCalendarPicker) {
        this.props.onToggleCalendarPicker(true);
      }
    }
  }

  hideCalendar() {
    if (this.state.showCalendar) {
      this.processCalendarPickerChanges();
      this.setState({ showCalendar: false, selectedDateField: this.props.selectedDateField });
      this.stopListeningForUndo();
      if (this.props.onToggleCalendarPicker) {
        this.props.onToggleCalendarPicker(false);
      }
    }
  }

  startListeningForUndo() {
    if (!this._pubSubUndoToken) {
      this._pubSubUndoToken = PubSub.subscribe(PubSubTopics.undoText, this.undoChange);
      this._pubSubRedoToken = PubSub.subscribe(PubSubTopics.redoText, this.redoChange);
      this.clearUndoStack(this.state.calendarPickerDates);
    }
  }

  stopListeningForUndo() {
    if (this._pubSubUndoToken) {
      PubSub.unsubscribe(this._pubSubUndoToken);
    }
    if (this._pubSubRedoToken) {
      PubSub.unsubscribe(this._pubSubRedoToken);
    }
    this._pubSubUndoToken = null;
    this._pubSubUndoToken = null;
  }

  // Handling our own local Undo
  clearUndoStack(value) {
    const selection = this.state.selectedDateField || DATE_FIELD_START;
    this._localUndoStack = localUndoUtils.reset(value, selection);
  }

  updateUndoStack(value) {
    const selection = this.state.selectedDateField || DATE_FIELD_START;
    this._localUndoStack = localUndoUtils.change(this._localUndoStack, value, selection);
  }

  updateUndoSelection(selection) {
    this._localUndoStack = localUndoUtils.moveSelection(this._localUndoStack, this.state.value, selection);
  }

  undoChange() {
    // If the active element is editable, then focus is inside a text field inside this element, so we want to let
    // undo and redo fall through to their handling instead of handling it ourselves
    const isActiveElementEditable = isEditable(document.activeElement);
    if (!isActiveElementEditable) {
      this._localUndoStack = localUndoUtils.undo(this._localUndoStack);
      this.applyPresentMoment();
    }
  }

  redoChange() {
    // If the active element is editable, then focus is inside a text field inside this element, so we want to let
    // undo and redo fall through to their handling instead of handling it ourselves
    const isActiveElementEditable = isEditable(document.activeElement);
    if (!isActiveElementEditable) {
      this._localUndoStack = localUndoUtils.redo(this._localUndoStack);
      this.applyPresentMoment();
    }
  }

  applyPresentMoment() {
    const moment = this._localUndoStack.present;
    const calendarPickerDates = moment.value;
    const selectedDateField = moment.selection;
    this.updateCalendarPickerDatesInternally(calendarPickerDates);
    this.setState({ selectedDateField });
  }

  processCalendarPickerChanges() {
    if (this._unprocessedCalendarPickerChanges) {
      const newDateValues = {
        startDate: this.state.calendarPickerDates.preferredStartDate,
        endDate: this.state.calendarPickerDates.preferredEndDate,
        latestStartDate: this.state.calendarPickerDates.latestStartDate,
        earliestEndDate: this.state.calendarPickerDates.earliestEndDate
      };

      const keys = DATE_FIELDS;
      keys.forEach(k => {
        newDateValues[k] = {
          timestamp: newDateValues[k] ? newDateValues[k].timestamp : null,
          precision: newDateValues[k] ? newDateValues[k].precision : null
        };
      });

      if (this.props.onBulkDateChange) {
        this.props.onBulkDateChange(newDateValues);
      }
    }
    this._unprocessedCalendarPickerChanges = false;
    this.clearUndoStack(this.state.calendarPickerDates);
  }

  onFocusField(field) {
    this.setState({ selectedDateField: field });
    this.updateUndoSelection(field);
  }

  render() {
    const isIos = preprocessorQuery.isIos();
    const {
      durationToEarliestEnd,
      duration,
      allowUncertainDates,
      calendar,
      isDual,
      disabled,
      dateForNow,
      onFocus,
      hasCalendarPicker,
      title,
      backText,
      startLabel,
      endLabel
    } = this.props;

    const selectedDateField = this.state.selectedDateField;

    const dateValues = {
      startDate: this.state.calendarPickerDates.preferredStartDate,
      endDate: this.state.calendarPickerDates.preferredEndDate,
      latestStartDate: this.state.calendarPickerDates.latestStartDate,
      earliestEndDate: this.state.calendarPickerDates.earliestEndDate
    };

    let dateComponent = null;
    let calendarIcon = "calendar";
    let calendarIconClass = "datePickerCalendarIcon";
    let calendarOnClick = this.showCalendar;
    const lockedIcon = "calendarLock";

    const noEntry = isIos && !this.props.withinCalendarPicker;

    if (noEntry) {
      dateComponent = (
        <span onClick={this.showCalendar}>{getValueWithNone(this.state.value, this.props.emptyLabel)}</span>
      );
      calendarIcon = "rightArrow";
      calendarIconClass = "datePickerIosArrowIcon";
      calendarOnClick = null;
    } else {
      dateComponent = (
        <FormTextField
          placeholder={this.props.emptyLabel || "None"}
          ref={this._fieldRef}
          value={this.state.value}
          disabled={Boolean(disabled)}
          onChange={this.onChange}
          onBlur={this.onBlur}
          continuouslyUpdates={true}
          onFocus={onFocus}
          noBlurOnCommit={this.props.noBlurOnCommit}
          allowEnterEvent={this.props.noBlurOnCommit}
        />
        // <input
        //   id={this.inputId}
        //   className="datePickerInput"
        //   placeholder={this.props.emptyLabel || "None"}
        //   type="text"
        //   ref={input => {
        //     this._input = input;
        //   }}
        //   value={this.state.value}
        //   onChange={disabled ? null : this.onChange}
        //   onBlur={disabled ? null : this.onBlur}
        //   disabled={Boolean(disabled)}
        //   onKeyDown={disabled ? null : this.onKeyDown}
        //   onFocus={onFocus}
        // />
      );
    }
    let calendarPopover = null;
    if (hasCalendarPicker && this.state.showCalendar && !disabled) {
      calendarPopover = (
        <Fragment>
          <CalendarPickerPopover
            isOpen={!disabled && this.state.showCalendar}
            target={this.props.targetId ? this.props.targetId : this.targetId}
            onOk={this.hideCalendar}
            title={
              title ? title : isDual ? getDateFieldTitle(this.state.selectedDateField, startLabel, endLabel) : "Date"
            }
            backText={backText}
          >
            <CalendarPicker
              calendar={calendar}
              {...dateValues}
              allowUncertainDates={allowUncertainDates}
              isDual={isDual}
              selectedDateField={selectedDateField}
              onStartDateChange={this.onCalendarPickerStartDateChange}
              onLatestStartChange={this.onCalendarPickerLatestStartDateChange}
              onEarliestEndChange={this.onCalendarPickerEarliestEndDateChange}
              onEndDateChange={this.onCalendarPickerEndDateChange}
              onPrecisionChange={this.onCalendarPickerPrecisionChange}
              dateForNow={dateForNow}
              durationToEarliestEnd={durationToEarliestEnd}
              duration={duration}
              onFocusField={this.onFocusField}
              startLabel={startLabel}
              endLabel={endLabel}
            />
          </CalendarPickerPopover>
          {this.props.useBackdrop ? <div className="formListBackdrop" onClick={this.hideCalendar} /> : null}
        </Fragment>
      );
    }

    return (
      <div className={`datePicker ${disabled ? "disabled" : ""}`}>
        {dateComponent}
        {wrapEditingComponent(
          hasCalendarPicker ? (
            <Fragment>
              <Icon
                icon={disabled ? lockedIcon : calendarIcon}
                className={calendarIconClass}
                onClick={calendarOnClick}
                id={!this.props.targetId ? this.targetId : null}
                tooltip="Show Calendar Picker"
              />
              {calendarPopover}
            </Fragment>
          ) : null
        )}
      </div>
    );
  }
}

FormDatePicker.defaultProps = {
  isDual: false,
  hasCalendarPicker: true,
  allowUncertainDates: true
};

FormDatePicker.propTypes = {
  startDate: PropTypeShapes.dateRelated,
  latestStartDate: PropTypeShapes.dateRelated,
  earliestEndDate: PropTypeShapes.dateRelated,
  endDate: PropTypeShapes.dateRelated,
  durationToEarliestEnd: PropTypeShapes.timespan,
  duration: PropTypeShapes.timespan,
  autoAdjustEndDate: PropTypes.bool,
  allowUncertainDates: PropTypes.bool.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  isDual: PropTypes.bool.isRequired,
  selectedDateField: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onStartDateChange: PropTypes.func,
  onLatestStartChange: PropTypes.func,
  onEarliestEndChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onBulkDateChange: PropTypes.func,

  validate: PropTypes.func,
  autoFocus: PropTypes.bool,
  noBlurOnCommit: PropTypes.bool,
  dateForNow: PropTypeShapes.date,
  hasCalendarPicker: PropTypes.bool.isRequired,
  withinCalendarPicker: PropTypes.bool,
  onFocus: PropTypes.func,
  onToggleCalendarPicker: PropTypes.func,
  title: PropTypes.string,
  backText: PropTypes.string,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  ongoing: PropTypes.bool,
  emptyLabel: PropTypes.string,
  targetId: PropTypes.string,
  useBackdrop: PropTypes.bool,

  // From withPortalChildContext
  portalChildContext: PropTypes.object
};

let Component = FormDatePicker;
//#!if iOS === true
// Component = withPortalChildContext(Component);
//#!endif

export default Component;
